import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import { advanceFrom } from '../../../tools/enterAdvancesFields';
import ConfirmDialog from '../../../tools/modal/confirmDialog';

// ── Loading places: the lists the packing list's origin and final destination are picked from ─────────────────────
//
// Two lists per branch ('origin' and 'destination'), kept on the server (misPackingPlace). The person picks a place
// from the list, or - when it is not there - types it and adds it, which puts it in the list for every later list.
// The packing list itself still stores the place as plain text, so removing an entry changes nothing already written.

// Mirrors api/utils/packingListFields.js placeKey() - what makes two names the same place. Keep the two in step.
const tidy = (v) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
const placeKey = (v) => tidy(v).toLowerCase().replace(/ي/g, 'ی').replace(/ك/g, 'ک').replace(/ـ/g, '');

const byName = (a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' });
const failure = (err, fallback) => (err && err.response && err.response.data && err.response.data.message) || fallback;

/** The two lists of a branch, and the calls that change them. */
export function usePackingPlaces(branchId, enabled) {
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const api = axiosGlobal.defaultTargetApi;
  const [places, setPlaces] = useState({ origin: [], destination: [] });

  useEffect(() => {
    if (!enabled || !branchId) return undefined;
    let cancelled = false;
    authCtx.jwtInst({ method: 'get', url: `${api}/mis/packing-places`, params: { branchId } })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        setPlaces({ origin: rows.filter((r) => r.kind === 'origin'), destination: rows.filter((r) => r.kind === 'destination') });
      })
      .catch(() => { /* an empty list is still usable: the person can type and add */ });
    return () => { cancelled = true; };
  }, [enabled, branchId, authCtx, api]);

  // Resolves with the stored place (the existing one when it was already there); rejects with the server's own message.
  const add = useCallback(async (kind, name) => {
    const res = await authCtx.jwtInst({ method: 'post', url: `${api}/mis/packing-places`, data: { branchId, kind, name } });
    const row = res.data;
    setPlaces((p) => (p[kind].some((r) => String(r._id) === String(row._id))
      ? p
      : { ...p, [kind]: [...p[kind], { _id: row._id, kind: row.kind, name: row.name }].sort(byName) }));
    return row;
  }, [authCtx, api, branchId]);

  const remove = useCallback(async (kind, id) => {
    await authCtx.jwtInst({ method: 'delete', url: `${api}/mis/packing-places/${id}` });
    setPlaces((p) => ({ ...p, [kind]: p[kind].filter((r) => String(r._id) !== String(id)) }));
  }, [authCtx, api]);

  return { places, add, remove };
}

// A combobox over one of the lists.
//
// The keyboard is the point of it (the list is opened and walked without the mouse):
//  · it OPENS when focus arrives - from the previous field with Enter, from a click, from Tab - so the places are
//    already on screen; with nothing typed, nothing is highlighted yet, so a stray Enter never picks one silently
//  · the arrows walk the places, Enter picks the highlighted one, and the focus goes ON to the next field (which, for
//    the origin, is the destination list: it opens in turn). Enter on an open list with nothing highlighted skips it
//  · type to narrow the list; a name that is not in it appears as "Add «name»" - Enter on it adds it to the list for
//    good and picks it. Escape closes the list and leaves the value alone
//  · a mouse pick does NOT move the focus: someone changing a place they already chose should not be thrown forward
export default function PlaceSelect({
  kind, label, value, onChange, rows, canManage, onAdd, onRemove, onError, touch = false, sx, helperText,
}) {
  const { t } = useTranslation();
  const rootRef = useRef(null);
  const addingRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [typing, setTyping] = useState('');     // what the person has typed; '' while the box merely shows the current value
  // The place the person asked to remove, waiting for them to confirm. Removing a place takes it out of the list for the
  // whole branch, so it is never done by one click on a small ✕.
  const [confirmRemove, setConfirmRemove] = useState(null);   // { id, name } | null

  // The current value as an option: the list's own entry when it is there, else the text itself (a list written before
  // there were lists keeps its place, shown as it was written).
  const current = useMemo(() => {
    const text = tidy(value);
    if (!text) return null;
    return rows.find((r) => placeKey(r.name) === placeKey(text)) || { _id: null, name: String(value), legacy: true };
  }, [value, rows]);
  const options = useMemo(() => (current && current.legacy ? [current, ...rows] : rows), [current, rows]);

  const filterOptions = (list, { inputValue }) => {
    const q = placeKey(inputValue);
    const shown = q ? list.filter((o) => placeKey(o.name).includes(q)) : list;
    const typed = tidy(inputValue);
    const exists = list.some((o) => placeKey(o.name) === q);
    return canManage && typed && !exists ? [...shown, { add: true, name: typed }] : shown;
  };

  // After the field's own handler has finished: moving focus from inside it would blur the field mid-handler.
  const goNext = () => setTimeout(() => advanceFrom(rootRef.current && rootRef.current.querySelector('input')), 0);

  const handleChange = async (event, option) => {
    if (!option) { onChange(''); return; }
    if (!option.add) { onChange(option.name); return; }
    addingRef.current = true;                         // handleClose runs next and must not move on before the add is done
    const fromKeyboard = Boolean(event && event.type === 'keydown');
    try {
      const row = await onAdd(kind, option.name);
      onChange(row.name);
    } catch (err) {
      addingRef.current = false;
      onError(failure(err, t('mis.plPlaceFailed')));
      return;
    }
    addingRef.current = false;
    if (fromKeyboard) goNext();
  };

  // Fires for every way the list closes, including picking the option that is ALREADY the value (which is not a
  // change, so onChange never hears of it) - which is why the move-on lives here and not in handleChange.
  const handleClose = (event, reason) => {
    setOpen(false);
    if (reason === 'selectOption' && event && event.type === 'keydown' && !addingRef.current) goNext();
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter' || event.isComposing || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return;
    const input = event.target;
    // an open list, nothing highlighted and nothing typed: nothing to pick - close it and move on
    if (open && input && !input.getAttribute('aria-activedescendant') && !tidy(input.value)) {
      event.preventDefault();
      event.defaultMuiPrevented = true;
      setOpen(false);
      goNext();
    }
  };

  const confirmed = () => {
    const target = confirmRemove;
    if (!target) return;
    onRemove(kind, target.id).catch((err) => onError(failure(err, t('mis.plPlaceFailed'))));
  };

  return (
    <>
    <Autocomplete
      ref={rootRef}
      fullWidth
      openOnFocus
      // never under the confirmation dialog: the list comes back when the dialog is closed and the focus returns here
      open={open && !confirmRemove}
      onOpen={() => setOpen(true)}
      onClose={handleClose}
      options={options}
      value={current}
      onChange={handleChange}
      onInputChange={(_, v, reason) => setTyping(reason === 'input' ? v : '')}
      onKeyDown={handleKeyDown}
      // typed text highlights its first match, so Enter takes it; with nothing typed nothing is highlighted
      autoHighlight={Boolean(typing.trim())}
      filterOptions={filterOptions}
      getOptionLabel={(o) => (o && o.name) || ''}
      isOptionEqualToValue={(o, v) => Boolean(o.add) === Boolean(v.add) && placeKey(o.name) === placeKey(v.name)}
      noOptionsText={t('mis.plPlaceNone')}
      // a menu belongs above whatever surface opened it (see the portal z-index note in CLAUDE.md)
      slotProps={{ popper: { sx: { zIndex: (theme) => theme.zIndex.modal + 2 } } }}
      ListboxProps={{ sx: { maxHeight: 280 } }}
      sx={{
        // on a phone the two small buttons at the end of the box (clear, and the list's arrow) are a finger's size
        ...(touch ? { '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': { width: 40, height: 40 } } : {}),
        ...sx,
      }}
      renderOption={(props, option) => (option.add ? (
        <Box component="li" {...props} key="__add__"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, ...(touch ? { minHeight: 48 } : {}) }}>
          <AddIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: touch ? '0.95rem' : '0.82rem', fontWeight: 600, overflowWrap: 'anywhere' }}>
            {t('mis.plPlaceAdd', { name: option.name })}
          </Typography>
        </Box>
      ) : (
        <Box component="li" {...props} key={option._id || `legacy:${option.name}`}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, ...(touch ? { minHeight: 48 } : {}) }}>
          <Typography sx={{ fontSize: touch ? '0.95rem' : '0.82rem', minWidth: 0, overflowWrap: 'anywhere' }}>{option.name}</Typography>
          {canManage && option._id && (
            <Tooltip title={t('mis.plPlaceRemove')}>
              {/* mouse only (tabIndex -1) and it must not pick the option it sits on */}
              <IconButton size="small" tabIndex={-1} aria-label={t('mis.plPlaceRemove')}
                sx={{ flexShrink: 0, ...(touch ? { width: 40, height: 40 } : {}) }}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  setOpen(false);                                   // the list gets out of the dialog's way
                  setConfirmRemove({ id: option._id, name: option.name });
                }}>
                <CloseIcon sx={{ fontSize: touch ? 18 : 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ))}
      renderInput={(params) => (
        <TextField {...params} size="small" label={label} helperText={helperText}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          inputProps={{ ...params.inputProps, 'data-place-kind': kind }} />
      )}
    />
    {/* Closing it hands the focus back to the field (MUI restores it), which opens the list again - without the place
        that was removed. The dialog sits above whatever surface opened the form. */}
    <ConfirmDialog
      open={Boolean(confirmRemove)}
      onClose={() => setConfirmRemove(null)}
      onConfirm={confirmed}
      destructive
      title={t('mis.plPlaceRemoveTitle')}
      message={t(kind === 'origin' ? 'mis.plPlaceRemoveOrigin' : 'mis.plPlaceRemoveDestination', { name: confirmRemove ? confirmRemove.name : '' })}
      confirmLabel={t('mis.plPlaceRemoveConfirm')}
      cancelLabel={t('common.cancel')}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
    />
    </>
  );
}
